import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FacebookLogo,
  InstagramLogo,
  EnvelopeSimple,
  Phone,
  MapPin,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Udfyld venligst navn, e-mail og besked.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Tak! Vi vender tilbage hurtigst muligt.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Noget gik galt. Prøv igen om lidt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      id="kontakt"
      data-testid="site-footer"
      className="relative border-t border-neutral-900 bg-black"
    >
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <span className="section-eyebrow">07 · Kontakt</span>
            <h2 className="mt-5 font-heading text-5xl uppercase leading-none tracking-tight text-white sm:text-6xl">
              Kom forbi.
              <br />
              Skriv til os.
            </h2>
            <p className="mt-4 max-w-md font-body text-neutral-400">
              Har du spørgsmål til medlemskab, sponsorater, udlejning af baner
              eller bare hilser? Udfyld formularen — vi svarer typisk inden for
              et par dage.
            </p>

            <div className="mt-10 space-y-4">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="mt-1 text-brand" />
                <div>
                  <div className="mono-label text-neutral-500">Adresse</div>
                  <div className="font-body text-white">
                    Nybolig Park — Taastrup Idrætspark
                    <br />
                    Parkvej, 2630 Taastrup
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <EnvelopeSimple size={18} className="mt-1 text-brand" />
                <div>
                  <div className="mono-label text-neutral-500">E-mail</div>
                  <a
                    href="mailto:sekretaer@taastrupfc.com"
                    className="font-body text-white hover:text-brand"
                    data-testid="footer-email"
                  >
                    sekretaer@taastrupfc.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone size={18} className="mt-1 text-brand" />
                <div>
                  <div className="mono-label text-neutral-500">Telefon (Kontoret)</div>
                  <a
                    href="tel:+4531903020"
                    className="font-body text-white hover:text-brand"
                    data-testid="footer-phone"
                  >
                    +45 31 90 30 20
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-10 flex gap-3">
              <a
                href="#"
                aria-label="Facebook"
                data-testid="footer-social-facebook"
                className="grid h-10 w-10 place-items-center border border-neutral-800 transition-colors hover:border-brand hover:text-brand"
              >
                <FacebookLogo size={18} />
              </a>
              <a
                href="#"
                aria-label="Instagram"
                data-testid="footer-social-instagram"
                className="grid h-10 w-10 place-items-center border border-neutral-800 transition-colors hover:border-brand hover:text-brand"
              >
                <InstagramLogo size={18} />
              </a>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="hairline bg-neutral-950 p-6 sm:p-8 lg:col-span-7"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="mono-label text-neutral-400">
                  Navn
                </label>
                <input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  data-testid="contact-input-name"
                  className="mt-2 w-full border border-neutral-800 bg-black px-3 py-3 font-body text-white outline-none transition-colors focus:border-brand"
                />
              </div>
              <div>
                <label htmlFor="email" className="mono-label text-neutral-400">
                  E-mail
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  data-testid="contact-input-email"
                  className="mt-2 w-full border border-neutral-800 bg-black px-3 py-3 font-body text-white outline-none transition-colors focus:border-brand"
                />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="subject" className="mono-label text-neutral-400">
                Emne
              </label>
              <input
                id="subject"
                name="subject"
                value={form.subject}
                onChange={onChange}
                data-testid="contact-input-subject"
                className="mt-2 w-full border border-neutral-800 bg-black px-3 py-3 font-body text-white outline-none transition-colors focus:border-brand"
              />
            </div>
            <div className="mt-5">
              <label htmlFor="message" className="mono-label text-neutral-400">
                Besked
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                value={form.message}
                onChange={onChange}
                data-testid="contact-input-message"
                className="mt-2 w-full resize-none border border-neutral-800 bg-black px-3 py-3 font-body text-white outline-none transition-colors focus:border-brand"
              />
            </div>
            <div className="mt-6 flex items-center justify-between gap-4">
              <p className="mono-label text-neutral-500">
                Vi læser alle beskeder.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="btn-primary disabled:opacity-60"
              >
                {loading ? "Sender…" : "Send besked"}
              </button>
            </div>
          </form>
        </div>

        {/* Massive wordmark */}
        <div
          aria-hidden="true"
          className="mt-24 select-none overflow-hidden border-t border-neutral-900 pt-10"
        >
          <div className="font-heading text-[18vw] leading-none tracking-tighter text-neutral-900">
            TAASTRUP FC
          </div>
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-4 border-t border-neutral-900 pt-6 sm:flex-row sm:items-center">
          <p className="mono-label text-neutral-500">
            © {new Date().getFullYear()} Taastrup FC · Udkast
          </p>
          <p className="mono-label text-neutral-600">
            Bygget med fællesskab og kode.
          </p>
        </div>
      </div>
    </footer>
  );
};
